/**
 * Enveloppe minimale d'IndexedDB — une centaine de lignes, sans Dexie.
 *
 * Ce n'est pas du purisme : les schémas d'accès sont triviaux parce que TOUT
 * est chargé en mémoire au démarrage (quelques milliers de lignes, quelques
 * centaines de Ko). Un `getAll` par magasin au boot, des `put` derrière.
 * Aucune requête indexée complexe n'est nécessaire, donc aucune bibliothèque
 * de requêtes ne l'est non plus — et c'est du poids en moins dans un bundle
 * qu'on précache pour l'usage hors-ligne.
 */

import { TABLES_SYNCHRONISEES } from '../../../db/schema.ts'

const NOM_BASE = 'popote'

/**
 * Version du schéma CLIENT.
 *
 * À incrémenter quand la FORME des lignes change. La migration est alors la
 * plus bête possible — vider les miroirs et refaire un pull complet, le
 * serveur faisant autorité. Voir la séquence de démarrage dans store.ts :
 * cette purge ne doit JAMAIS emporter l'outbox.
 */
export const VERSION_SCHEMA_CLIENT = 1

/** Magasins qui ne sont PAS des miroirs, et qu'aucune migration ne purge. */
export const MAGASINS_LOCAUX = ['meta', 'outbox', 'conflits'] as const

function promesse<T>(requete: IDBRequest<T>): Promise<T> {
  return new Promise((resoudre, rejeter) => {
    requete.onsuccess = () => resoudre(requete.result)
    requete.onerror = () => rejeter(requete.error)
  })
}

let ouverture: Promise<IDBDatabase> | null = null

export function ouvrir(): Promise<IDBDatabase> {
  if (ouverture) return ouverture
  ouverture = new Promise((resoudre, rejeter) => {
    const requete = indexedDB.open(NOM_BASE, VERSION_SCHEMA_CLIENT)
    requete.onupgradeneeded = () => {
      const base = requete.result
      for (const nom of [...MAGASINS_LOCAUX, ...TABLES_SYNCHRONISEES]) {
        if (!base.objectStoreNames.contains(nom)) {
          base.createObjectStore(nom, { keyPath: nom === 'meta' ? 'cle' : nom === 'outbox' ? 'opId' : 'id' })
        }
      }
    }
    requete.onsuccess = () => resoudre(requete.result)
    requete.onerror = () => rejeter(requete.error)
    requete.onblocked = () =>
      rejeter(new Error('Une autre fenêtre de Popote est ouverte sur une version différente.'))
  })
  return ouverture
}

export async function tousLes<T>(magasin: string): Promise<T[]> {
  const base = await ouvrir()
  const tx = base.transaction(magasin, 'readonly')
  return promesse(tx.objectStore(magasin).getAll() as IDBRequest<T[]>)
}

export async function ecrire(
  magasins: readonly string[],
  travail: (tx: IDBTransaction) => void,
): Promise<void> {
  const base = await ouvrir()
  const tx = base.transaction(magasins as string[], 'readwrite')
  travail(tx)
  return new Promise((resoudre, rejeter) => {
    tx.oncomplete = () => resoudre()
    tx.onerror = () => rejeter(tx.error)
    tx.onabort = () => rejeter(tx.error ?? new Error('Transaction IndexedDB annulée'))
  })
}

export async function lireMeta<T>(cle: string, defaut: T): Promise<T> {
  const base = await ouvrir()
  const tx = base.transaction('meta', 'readonly')
  const r = await promesse(tx.objectStore('meta').get(cle) as IDBRequest<{ valeur: T } | undefined>)
  return r ? r.valeur : defaut
}

export async function ecrireMeta(cle: string, valeur: unknown): Promise<void> {
  return ecrire(['meta'], (tx) => {
    tx.objectStore('meta').put({ cle, valeur })
  })
}

/**
 * Vide les MIROIRS uniquement, jamais l'outbox ni les métadonnées.
 *
 * Ce garde-fou est le plus important du fichier : une purge qui emporterait
 * l'outbox perdrait les courses de quelqu'un, en silence et sans recours.
 * Aucun autre endroit du code n'a le droit d'appeler `clear()` sur `outbox`.
 */
export async function viderMiroirs(): Promise<void> {
  return ecrire(TABLES_SYNCHRONISEES, (tx) => {
    for (const nom of TABLES_SYNCHRONISEES) tx.objectStore(nom).clear()
  })
}
